import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Globe, Phone, Mail, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import SEO, { lodgingSchema, breadcrumbSchema } from "@/components/seo/SEO";
import { REGISTRATION_URL } from "@/lib/config";

const CATEGORY_LABEL = {
    hotel: "Hotel",
    camping: "Camping",
    hostal: "Hostal",
    apartamento: "Apartamento turístico",
    otro: "Alojamiento",
};

export default function DirectoryDetail() {
    const { slug } = useParams();
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setNotFound(false);
            try {
                const { data } = await api.get(`/directory/${slug}`);
                if (alive) setMember(data);
            } catch (e) {
                if (alive) setNotFound(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [slug]);

    if (loading) {
        return (
            <div data-testid="directory-detail-loading" className="py-32 text-center text-[#4A5568]">
                Cargando alojamiento...
            </div>
        );
    }

    if (notFound || !member) {
        return (
            <div className="bg-white">
                <SEO
                    title="Alojamiento no encontrado"
                    description="El alojamiento que buscas no está disponible en nuestro directorio."
                    path={`/directorio/${slug}`}
                    noIndex
                />
                <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24 text-center">
                    <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                        Directorio
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                        Alojamiento no encontrado
                    </h1>
                    <p className="mt-4 text-[#4A5568]">
                        Es posible que el alojamiento haya sido retirado del directorio o
                        que la URL no sea correcta.
                    </p>
                    <Link
                        to="/directorio"
                        data-testid="btn-back-directory"
                        className="mt-8 inline-flex items-center gap-2 border border-[#082050] text-[#082050] px-6 py-3 text-sm font-medium hover:bg-[#082050]/5"
                    >
                        <ArrowLeft size={14} />
                        Volver al directorio
                    </Link>
                </section>
            </div>
        );
    }

    const categoryLabel = CATEGORY_LABEL[member.category] || "Alojamiento";
    const title = `${member.name} · ${categoryLabel} en Tarifa`;
    const description = member.description
        ? `${member.description} ${member.location ? `Ubicado en ${member.location}.` : ""} Miembro asociado a ASATTA.`
        : `${categoryLabel} en Tarifa asociado a ASATTA. ${member.location || ""}`.trim();

    return (
        <div data-testid="directory-detail-page" className="bg-white">
            <SEO
                title={title}
                description={description}
                path={`/directorio/${member.slug}`}
                image={member.logo}
                jsonLd={[
                    lodgingSchema(member),
                    breadcrumbSchema([
                        { name: "Inicio", path: "/" },
                        { name: "Directorio", path: "/directorio" },
                        { name: member.name, path: `/directorio/${member.slug}` },
                    ]),
                ]}
            />

            <nav
                aria-label="Breadcrumb"
                className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-xs text-[#718096]"
            >
                <ol className="flex items-center gap-2 flex-wrap">
                    <li>
                        <Link to="/" className="hover:text-[#082050]">Inicio</Link>
                    </li>
                    <li aria-hidden="true">/</li>
                    <li>
                        <Link to="/directorio" className="hover:text-[#082050]">
                            Directorio
                        </Link>
                    </li>
                    <li aria-hidden="true">/</li>
                    <li className="text-[#082050] truncate">{member.name}</li>
                </ol>
            </nav>

            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Imagen */}
                    <div className="lg:col-span-6">
                        <div className="aspect-[4/3] overflow-hidden bg-[#F8F9FA] border border-[#E2E4E9]">
                            {member.logo ? (
                                <img
                                    src={member.logo}
                                    alt={`Fotografía de ${member.name}, ${categoryLabel.toLowerCase()} en ${member.location || "Tarifa"}`}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-[#718096]">
                                    Imagen no disponible
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="lg:col-span-6">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-3">
                            {categoryLabel} · Tarifa
                        </div>
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#082050] tracking-tight">
                            {member.name}
                        </h1>
                        {member.description && (
                            <p className="mt-6 text-[#4A5568] text-base leading-relaxed">
                                {member.description}
                            </p>
                        )}

                        <div className="mt-8 space-y-4 text-sm text-[#4A5568]">
                            {member.location && (
                                <div className="flex items-start gap-3">
                                    <MapPin size={16} className="text-[#082050] mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-[#082050]/60">Ubicación</div>
                                        <div>{member.location}</div>
                                    </div>
                                </div>
                            )}
                            {member.phone && (
                                <div className="flex items-start gap-3">
                                    <Phone size={16} className="text-[#082050] mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-[#082050]/60">Teléfono</div>
                                        <a
                                            href={`tel:${member.phone.replace(/\s/g, "")}`}
                                            className="hover:text-[#082050]"
                                        >
                                            {member.phone}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {member.email && (
                                <div className="flex items-start gap-3">
                                    <Mail size={16} className="text-[#082050] mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-[#082050]/60">Email</div>
                                        <a
                                            href={`mailto:${member.email}`}
                                            className="hover:text-[#082050] break-all"
                                        >
                                            {member.email}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {member.website && (
                                <div className="flex items-start gap-3">
                                    <Globe size={16} className="text-[#082050] mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-[#082050]/60">Web oficial</div>
                                        <a
                                            href={member.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:text-[#082050] break-all underline"
                                        >
                                            {member.website.replace(/^https?:\/\//, "")}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>

                        {member.website && (
                            <a
                                href={member.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="btn-visit-website"
                                className="mt-8 inline-flex items-center gap-2 bg-[#082050] text-white px-6 py-3 text-sm font-medium hover:bg-[#082050]/90"
                            >
                                Visitar web oficial
                                <ArrowRight size={14} />
                            </a>
                        )}
                    </div>
                </div>
            </section>

            {/* Sección CTA para captación */}
            <section className="border-t border-[#E2E4E9] bg-[#F8F9FA]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-3">
                            ¿Gestionas un alojamiento en Tarifa?
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-[#082050] tracking-tight">
                            Únete a la asociación que representa al sector.
                        </h2>
                        <p className="mt-4 text-[#4A5568]">
                            Forma parte del directorio, gana visibilidad y participa en
                            los proyectos que mueven el turismo de calidad de Tarifa.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <a
                            href={REGISTRATION_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-detail-hazte-socio"
                            className="inline-flex items-center gap-2 bg-[#082050] text-white px-6 py-3 text-sm font-medium hover:bg-[#082050]/90"
                        >
                            Hazte socio
                            <ArrowRight size={14} />
                        </a>
                        <Link
                            to="/directorio"
                            data-testid="btn-back-directory-cta"
                            className="inline-flex items-center gap-2 border border-[#082050] text-[#082050] px-6 py-3 text-sm font-medium hover:bg-[#082050]/5"
                        >
                            <ArrowLeft size={14} />
                            Ver todos los alojamientos
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
